import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, Monitor } from "lucide-react";
import Reveal from "@/components/Reveal";
import { INTERNSHIPS, TECHNICAL_DOMAINS, NON_TECHNICAL_DOMAINS } from "@/data/internships";

export default function Internships() {
  return (
    <div className="pt-24 md:pt-32">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">
            <span className="inline-block w-6 h-px bg-neutral-400" />
            Internship Programs
          </span>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-5xl">
            Programs curated for the next generation of builders.
          </h1>
          <p className="mt-8 text-lg text-neutral-600 max-w-3xl leading-relaxed">
            High-quality programs curated for students to gain real-time experience, build in-demand
            skills, and shape their professional careers with expert guidance.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {INTERNSHIPS.map((it, idx) => (
            <Reveal key={it.slug} delay={idx * 60}>
              <article className="group bg-white border border-neutral-200 rounded-2xl overflow-hidden flex flex-col h-full hover:border-neutral-900 transition-colors">
                <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                  <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full">
                      {it.tag}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl tracking-tight leading-tight">{it.title}</h3>
                  <div className="mt-4 space-y-1.5 text-xs text-neutral-600">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-neutral-400" strokeWidth={1.5} />
                      <div><span className="text-neutral-500">Location: </span>{it.location}</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Clock className="w-3.5 h-3.5 mt-0.5 text-neutral-400" strokeWidth={1.5} />
                      <div><span className="text-neutral-500">Duration: </span>{it.duration}</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Monitor className="w-3.5 h-3.5 mt-0.5 text-neutral-400" strokeWidth={1.5} />
                      <div><span className="text-neutral-500">Work Mode: </span>{it.mode}</div>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-neutral-600 leading-relaxed">{it.short}</p>
                  <div className="mt-6 pt-6 border-t border-neutral-100 flex items-center justify-between">
                    <Link to={`/internships/${it.slug}`} data-testid={`ip-read-${it.slug}`} className="text-sm font-medium hover:opacity-70 inline-flex items-center gap-1">
                      Read More <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </Link>
                    <Link to={`/apply?domain=${encodeURIComponent(it.title)}`} data-testid={`ip-enroll-${it.slug}`} className="btn-pill btn-pill--primary text-xs" style={{ padding: "10px 18px" }}>
                      Enroll Now
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#FAFAFA] border-t border-neutral-100 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">
                <span className="inline-block w-6 h-px bg-neutral-400" />
                Programs Offered
              </span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Internship domains & opportunities
              </h2>
              <p className="mt-6 text-neutral-600 leading-relaxed">
                Kickstart your professional journey with our industry-led internship programs across
                technical and non-technical tracks.
              </p>
              <div className="mt-8 space-y-4 text-sm text-neutral-700">
                <MetaRow label="Duration" value="Up to 6 Months" />
                <MetaRow label="Who Can Apply" value="College Students · Recent Graduates" />
                <MetaRow label="Eligibility" value="BE · BCA · BCom · MBA · MCA · BBA · Diploma" />
              </div>
              <Link to="/apply" className="btn-pill btn-pill--primary mt-10">
                Apply Now
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
              <DomainCard title="Technical Domains" items={TECHNICAL_DOMAINS} />
              <DomainCard title="Non-Technical Domains" items={NON_TECHNICAL_DOMAINS} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function DomainCard({ title, items }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-8">
      <h3 className="font-serif text-2xl tracking-tight">{title}</h3>
      <ul className="mt-6 flex flex-wrap gap-2">
        {items.map((it) => (
          <li key={it} className="text-xs px-3 py-2 rounded-full border border-neutral-200 text-neutral-700 hover:border-neutral-900 transition-colors">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-t border-neutral-200 pt-4">
      <span className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</span>
      <span className="col-span-2 text-neutral-800">{value}</span>
    </div>
  );
}
